import { Injectable, Type } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AlertService } from '../../../../../../core/services/alert/alert.service';
import { ApplicationTemplateResponse } from '../../models/application-template-response';
import { ApplicationCreationResponse } from '../../../../shared/models/responses/application-creation-response';
import { StepNavigationService } from '../../../../shared/services/step-navigation/step-navigation.service';
import { hasOpenDialogs } from '../../../../../../core/functions/dialog-helper';
import { ApplicationTemplateFormDialogConfig } from './constants/application-template-form-dialog-config';
import { ApplicationTemplateModificationFormComponent } from '../../../components/application-template-modification-form/application-template-modification-form.component';
import { ApplicationTemplateCreationFormComponent } from '../../../components/application-template-creation-form/application-template-creation-form.component';
import { ApplicationTemplateCreationFromApplicationFormComponent } from '../../../components/application-template-creation-from-application-form/application-template-creation-from-application-form.component';

@Injectable({
    providedIn: 'root',
})
export class ApplicationTemplateFormDialogService {
    constructor(
        private stepNavigationService: StepNavigationService,
        private readonly alertService: AlertService,
        private readonly dialog: MatDialog,
    ) {}

    /**
     * Handles the display of the creation from application form dialog.
     */
    public displayCreationFromApplicationFormDialog(): void {
        if (hasOpenDialogs(this.dialog)) return;

        const dialogRef: MatDialogRef<
            ApplicationTemplateCreationFromApplicationFormComponent,
            ApplicationCreationResponse | null
        > = this.openTemplateCreationDialog(
            ApplicationTemplateCreationFromApplicationFormComponent,
        );

        dialogRef
            .afterClosed()
            .subscribe(
                (
                    applicationCreationResponse:
                        | ApplicationCreationResponse
                        | null
                        | undefined,
                ) => {
                    this.handleCreationFromApplicationDialogResult(
                        applicationCreationResponse,
                    );
                },
            );
    }

    /**
     * Handles the display of the creation form dialog and post dialoged actions.
     */
    public displayCreationFormDialog(): void {
        if (hasOpenDialogs(this.dialog)) return;

        const dialogRef: MatDialogRef<
            ApplicationTemplateCreationFormComponent,
            ApplicationCreationResponse | null
        > = this.openTemplateCreationDialog(
            ApplicationTemplateCreationFormComponent,
        );

        dialogRef
            .afterClosed()
            .subscribe(
                (
                    applicationCreationResponse:
                        | ApplicationCreationResponse
                        | null
                        | undefined,
                ) => {
                    this.handleCreationDialogResult(
                        applicationCreationResponse,
                    );
                },
            );
    }

    /**
     * Handles the display of the modification form dialog and post dialog actions.
     */
    public displayModificationFormDialog(): void {
        if (hasOpenDialogs(this.dialog)) return;

        const dialogRef: MatDialogRef<
            ApplicationTemplateModificationFormComponent,
            ApplicationTemplateResponse | null
        > = this.openTemplateModificationDialog(
            ApplicationTemplateModificationFormComponent,
        );

        dialogRef
            .afterClosed()
            .subscribe(
                (response: ApplicationTemplateResponse | null = null) => {
                    this.handleModificationDialogResult(response);
                },
            );
    }

    private handleCreationFromApplicationDialogResult(
        applicationCreationResponse:
            | ApplicationCreationResponse
            | null
            | undefined,
    ): void {
        if (applicationCreationResponse) {
            this.alertService.displaySuccessAlert(
                'Template information has been successfully created.',
                false,
                {
                    durationInMs: 5000,
                },
            );
        }
    }

    private handleCreationDialogResult(
        applicationCreationResponse:
            | ApplicationCreationResponse
            | null
            | undefined,
    ): void {
        if (applicationCreationResponse) {
            this.stepNavigationService
                .setDirectEntrySteps(
                    applicationCreationResponse.applicationId!.toString(),
                )
                .subscribe(() => {
                    this.stepNavigationService.navigateToFirstStep();
                });
        }
    }

    private handleModificationDialogResult(
        response: ApplicationTemplateResponse | null,
    ): void {
        if (response) {
            this.alertService.displaySuccessAlert(
                'Template information has been successfully saved.',
                false,
                {
                    durationInMs: 5000,
                },
            );
        }
    }

    /**
     * Opens the template creation dialog with the standard configuration for it.
     * @returns The template creation dialog reference
     */
    private openTemplateCreationDialog<
        T extends
            | ApplicationTemplateCreationFormComponent
            | ApplicationTemplateCreationFromApplicationFormComponent,
    >(component: Type<T>): MatDialogRef<T, ApplicationCreationResponse | null> {
        return this.dialog.open(
            component,
            ApplicationTemplateFormDialogConfig.defaultDialogConfig,
        );
    }

    /**
     * Opens the template creation dialog with the standard configuration for it.
     * @returns The template creation dialog reference
     */
    private openTemplateModificationDialog<
        T extends ApplicationTemplateModificationFormComponent,
    >(component: Type<T>): MatDialogRef<T, ApplicationTemplateResponse | null> {
        return this.dialog.open(
            component,
            ApplicationTemplateFormDialogConfig.defaultDialogConfig,
        );
    }
}
